import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar, Search } from 'lucide-react';

const LogFilterModal = ({ isOpen, onClose, onApply, currentFilter }) => {
  const [timeRangeType, setTimeRangeType] = useState('relative');
  const [filterData, setFilterData] = useState({
    duration: '30',
    unitOfTime: 'minutes',
    startDateTime: '',
    endDateTime: '',
    filterPattern: ''
  });

  // Initialize form data when modal opens or currentFilter changes
  useEffect(() => {
    if (currentFilter) {
      if (currentFilter.timeRange?.type === 'relative') {
        setTimeRangeType('relative');
        setFilterData({
          ...filterData,
          duration: currentFilter.timeRange.duration.toString(),
          unitOfTime: currentFilter.timeRange.unitOfTime,
          filterPattern: currentFilter.filterPattern || ''
        });
      } else if (currentFilter.timeRange?.type === 'absolute') {
        setTimeRangeType('absolute');
        const startDateTime = currentFilter.timeRange.startTime ? new Date(currentFilter.timeRange.startTime) : new Date();
        const endDateTime = currentFilter.timeRange.endTime ? new Date(currentFilter.timeRange.endTime) : new Date();
        
        setFilterData({
          ...filterData,
          startDateTime: formatDateTime(startDateTime),
          endDateTime: formatDateTime(endDateTime),
          filterPattern: currentFilter.filterPattern || ''
        });
      }
    }
  }, [isOpen, currentFilter]);

  const timeUnits = ['minutes', 'hours', 'days'];

  const formatDateTime = (date) => {
    // Format date to YYYY-MM-DDThh:mm format for datetime-local input
    return date.toISOString().slice(0, 16);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const filterPayload = {
      filterPattern: filterData.filterPattern,
      timeRange: timeRangeType === 'relative' 
        ? {
            type: 'relative',
            duration: parseInt(filterData.duration),
            unitOfTime: filterData.unitOfTime
          }
        : {
            type: 'absolute',
            startTime: filterData.startDateTime,
            endTime: filterData.endDateTime
          }
    };
    
    onApply(filterPayload);
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>

      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          <div className="bg-gray-900 px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-medium text-white">
              Filter Logs
            </h3>
            <button
              onClick={handleCancel}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">Time Range</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="relative"
                      checked={timeRangeType === 'relative'}
                      onChange={(e) => setTimeRangeType(e.target.value)}
                      className="mr-2"
                    />
                    <Clock className="w-4 h-4 mr-1" />
                    Relative
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="absolute"
                      checked={timeRangeType === 'absolute'}
                      onChange={(e) => setTimeRangeType(e.target.value)}
                      className="mr-2"
                    />
                    <Calendar className="w-4 h-4 mr-1" />
                    Absolute
                  </label>
                </div>
              </div>

              {timeRangeType === 'relative' && (
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-700 text-sm font-medium block mb-1">Duration</label>
                    <input
                      type="number"
                      name="duration"
                      value={filterData.duration}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Up to 4 digits"
                      min="1"
                      max="9999"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-gray-700 text-sm font-medium block mb-1">Unit of Time</label>
                    <select
                      name="unitOfTime"
                      value={filterData.unitOfTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    >
                      {timeUnits.map(unit => (
                        <option key={unit} value={unit}>{unit.charAt(0).toUpperCase() + unit.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {timeRangeType === 'absolute' && (
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-700 text-sm font-medium block mb-1">Start Date & Time</label>
                    <input
                      type="datetime-local"
                      name="startDateTime"
                      value={filterData.startDateTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-gray-700 text-sm font-medium block mb-1">End Date & Time</label>
                    <input
                      type="datetime-local"
                      name="endDateTime"
                      value={filterData.endDateTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">Keywords</label>
                <div className="relative">
                  <input
                    type="text"
                    name="filterPattern"
                    value={filterData.filterPattern}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter keyword..."
                  />
                  <Search className="absolute right-3 top-2.5 text-gray-400" size={18} />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Apply
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogFilterModal;